import { IParameter } from '@/modules/gpt-chats/types/tools.service.type';
import { ChevronDown, ChevronRight, PlusCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { ChipsInput, ChipsInputField, ChipsInputList } from '@/components/chip-input/chips-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { getValue, getInitialValue } from '../utils/value-utils';
import { SYSTEM_VARIABLE_OPTIONS } from '../utils/system-variable-utils';

interface DebugParameterRowProps {
  param: IParameter;
  keyOrIndex: string | number;
  path: (string | number)[];
  level: number;
  debugData: any;
  expandedPaths: Record<string, boolean>;
  onToggleExpand: (pathStr: string) => void;
  onValueChange: (path: (string | number)[], value: any) => void;
}

export const DebugParameterRow = ({
  param,
  keyOrIndex,
  path,
  level,
  debugData,
  expandedPaths,
  onToggleExpand,
  onValueChange,
}: DebugParameterRowProps) => {
  const currentValue = getValue(debugData, path);
  const displayName =
    typeof keyOrIndex === 'number' ? `Item ${keyOrIndex + 1}` : param.Name || keyOrIndex;

  const paramType = param.Type?.toLowerCase();
  const itemType = param.Items?.Type?.toLowerCase();
  const isArrayOfObjects = paramType === 'array' && itemType === 'object';
  const pathStr = path.join('-');
  const isExpanded = expandedPaths[pathStr] !== false;
  const isComplex = paramType === 'object' || isArrayOfObjects;

  return (
    <div className="contents">
      <div className="grid grid-cols-[1.5fr_1fr_1.5fr] items-center gap-2 border-b p-2 sm:gap-4">
        <div
          className="flex items-center gap-2 overflow-hidden pl-[calc(var(--level)*10px)] sm:pl-[calc(var(--level)*24px)]"
          style={{ '--level': level } as React.CSSProperties}
        >
          {isComplex && (
            <button
              onClick={() => onToggleExpand(pathStr)}
              className="rounded-full p-1 hover:bg-muted"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          )}
          {!isComplex && level > 0 && <span className="w-4" />}

          <span className="truncate text-sm font-medium" title={String(displayName)}>
            {displayName}
          </span>
        </div>

        <div
          className="truncate text-sm text-muted-foreground"
          title={param.Type === 'variable' ? 'System Variable' : param.Type}
        >
          {param.Type === 'variable' ? 'System Variable' : param.Type}
        </div>

        <div className="flex items-center gap-2">
          {paramType === 'array' ? (
            isArrayOfObjects ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-primary"
                  onClick={() => {
                    const currentArr = Array.isArray(currentValue) ? currentValue : [];
                    const newItem = param.Items ? getInitialValue(param.Items) : {};
                    onValueChange(path, [...currentArr, newItem]);
                    if (!isExpanded) onToggleExpand(pathStr);
                  }}
                >
                  <PlusCircle className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground">
                  {Array.isArray(currentValue) ? currentValue.length : 0} items
                </span>
              </div>
            ) : (
              <ChipsInput
                value={Array.isArray(currentValue) ? currentValue.map((v) => String(v)) : []}
                onChange={(val) => {
                  let finalVal: any[] = val;
                  if (itemType === 'integer' || itemType === 'number') {
                    finalVal = val.map((v) => Number(v)).filter((n) => !isNaN(n));
                  } else if (itemType === 'boolean') {
                    finalVal = val.map((v) => v === 'true');
                  }
                  onValueChange(path, finalVal);
                }}
                className="max-h-[100px] min-h-[32px] w-full overflow-y-auto"
              >
                <ChipsInputList />
                <ChipsInputField />
              </ChipsInput>
            )
          ) : paramType === 'object' ? (
            <></>
          ) : paramType === 'boolean' ? (
            <Select
              value={String(currentValue)}
              onValueChange={(val) => onValueChange(path, val === 'true')}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">True</SelectItem>
                <SelectItem value="false">False</SelectItem>
              </SelectContent>
            </Select>
          ) : paramType === 'variable' ? (
            <Select
              value={String(currentValue ?? '')}
              onValueChange={(val) => onValueChange(path, val)}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Select variable" />
              </SelectTrigger>
              <SelectContent>
                {SYSTEM_VARIABLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              className="h-8"
              type={paramType === 'integer' || paramType === 'number' ? 'number' : 'text'}
              value={String(currentValue ?? '')}
              onChange={(e) => {
                let val: any = e.target.value;
                if (paramType === 'integer' || paramType === 'number') {
                  if (val === '') val = undefined;
                  else val = Number(val);
                }
                onValueChange(path, val);
              }}
              placeholder={`Enter ${param.Type}`}
            />
          )}

          {typeof keyOrIndex === 'number' && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                const parentPath = path.slice(0, -1);
                const index = Number(path[path.length - 1]);
                const parentArray = getValue(debugData, parentPath) as any[];
                if (Array.isArray(parentArray)) {
                  const newArr = parentArray.filter((_, i) => i !== index);
                  onValueChange(parentPath, newArr);
                }
              }}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      </div>

      {isExpanded && paramType === 'object' && param.Properties && (
        <DebugParameterList
          schema={param.Properties}
          path={path}
          level={level + 1}
          debugData={debugData}
          expandedPaths={expandedPaths}
          onToggleExpand={onToggleExpand}
          onValueChange={onValueChange}
        />
      )}

      {isExpanded &&
        paramType === 'array' &&
        isArrayOfObjects &&
        Array.isArray(currentValue) &&
        param.Items &&
        currentValue.map((_, idx) => (
          <DebugParameterRow
            key={`${path.join('-')}-item-${idx}`}
            param={param.Items as IParameter}
            keyOrIndex={idx}
            path={[...path, idx]}
            level={level + 1}
            debugData={debugData}
            expandedPaths={expandedPaths}
            onToggleExpand={onToggleExpand}
            onValueChange={onValueChange}
          />
        ))}
    </div>
  );
};

export const DebugParameterList = ({
  schema,
  path = [],
  level = 0,
  debugData,
  expandedPaths,
  onToggleExpand,
  onValueChange,
}: {
  schema: Record<string, IParameter>;
  path?: (string | number)[];
  level?: number;
  debugData: any;
  expandedPaths: Record<string, boolean>;
  onToggleExpand: (pathStr: string) => void;
  onValueChange: (path: (string | number)[], value: any) => void;
}) => {
  return (
    <>
      {Object.entries(schema)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, param]) => (
          <DebugParameterRow
            key={key}
            param={param}
            keyOrIndex={key}
            path={[...path, key]}
            level={level}
            debugData={debugData}
            expandedPaths={expandedPaths}
            onToggleExpand={onToggleExpand}
            onValueChange={onValueChange}
          />
        ))}
    </>
  );
};
